/**
 * Origin identity — the ONLY place a provider label, transport name or deep
 * link is derived on the frontend.
 *
 * Five surfaces previously derived these independently from `source`,
 * `provider`, `mailbox_provider` and `external_id`: the row's provider chip,
 * the reader's chip, the composer's "via …" transport label, the unified row's
 * mailbox-owner chip, and the deep link. That divergence produced #521's badge
 * mislabel and #596's dead Outlook link — a link built for every message with
 * no provider branching, so every Gmail-sourced thread in a pooled inbox
 * rendered an `outlook.office.com` URL under a label naming the wrong product.
 *
 * The object these read is resolved SERVER-side, per message, and arrives as
 * `mail.origin` (see runtime/backend/services/origin_identity.py). Nothing
 * here re-derives anything; it only reads.
 */

/** The provider chip text — `Gmail`, `Outlook`, `NurtureCRM`, or null. */
export function providerChip(origin) {
    return origin?.provider_label ?? null;
}

/** The compose transport label — `Gmail API`, `Microsoft Graph`, or null. */
export function transportLabel(origin) {
    return origin?.transport_label ?? null;
}

/**
 * The open-in-provider control, or null when there is no provider copy.
 *
 * **Absent, never dead.** Returning null means the caller renders NO control —
 * not a disabled one, and certainly not a link that 404s. That single rule is
 * what #596 was actually asking for: the capability was fine, the link builder
 * was not.
 */
export function openInProvider(origin) {
    if (!origin?.web_link) return null;
    return {
        href: origin.web_link,
        label: `Open in ${origin.provider_label}`,
    };
}

/**
 * Whether mail state written here reaches the user's real mailbox.
 *
 * False for every Gmail row — the OAuth scope list is frozen at
 * `gmail.readonly` + `gmail.send` and deliberately excludes `gmail.modify`, so
 * a CRM star cannot be written back to Gmail. The UI uses this to say "CRM
 * only" rather than implying a change to the user's mailbox that never
 * happened. True for Outlook, where `Mail.ReadWrite` is granted.
 */
export function syncsToProvider(origin) {
    return Boolean(origin?.provider_sync);
}

/**
 * Tooltip for a mail-state control, naming where the change will land.
 *
 * An affordance that silently does less than it appears to is the defect class
 * this whole contract exists to prevent, so the control says so itself.
 */
export function mailStateScopeHint(origin, action = 'This') {
    if (!origin) return null;
    return syncsToProvider(origin)
        ? `${action} also updates ${origin.provider_label}`
        : `${action} applies in NurtureCRM only`;
}
